import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import styled, { css } from 'styled-components';
import { toJS } from 'mobx';

const darkBackground = '#222';
const lightText = 'var(--lightPrimaryRoi)';
const disabledBackground = '#3B3A3A';
const disabledText = 'grey';
const primaryColor = 'var(--primary)';
const borderRadius = '6px';

// Styled Components
const Wrapper = styled.div`
  position: relative;
  width: 100%;
  font-family: 'Roboto', sans-serif;

  
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.1rem 0.5rem;
  border: none;
  border-radius: ${borderRadius};
  cursor: pointer;
  background-color: ${darkBackground};
  transition: all 0.2s ease;
  color: ${lightText};
  min-height: 42px;
  
  
  ${props => props.isDisabled && css`
    background-color: ${disabledBackground};
    cursor: not-allowed;
    opacity: 0.7;
    color: ${disabledText};
  `}
  
  &:hover:not([disabled]) {
    border-color: ${primaryColor};
  }
  
  .automation-form-dropdown-multi & {
    font-size: 0.9rem;
    border-radius: 8px;
  }
`;

const Display = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.85rem;
  color: ${props => props.isEmpty ? disabledText : lightText};
`;

const ArrowWrapper = styled.div`
  margin-left: 0.5rem;
  display: flex;
  align-items: center;
`;

const Arrow = styled.svg`
  width: 18px;
  height: 18px;
  transition: transform 0.2s ease;
  color: ${lightText};
  
  ${props => props.rotated && css`
    transform: rotate(180deg);
  `}
`;

const DROPDOWN_MAX_HEIGHT = 460;
const DROPDOWN_GAP = 4;

const Dropdown = styled.div`
  position: absolute;
  z-index: 1000;
  width: 100%;
  background-color: ${darkBackground};
  border: 1px solid #333;
  border-radius: ${borderRadius};
  box-shadow: 0 4px 12px -2px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  max-height: ${props => props.maxHeight || DROPDOWN_MAX_HEIGHT}px;
  display: flex;
  flex-direction: column;
  color: ${lightText};

  ${props =>
    props.openUpward
      ? css`
          bottom: calc(100% + ${DROPDOWN_GAP}px);
        `
      : css`
          top: calc(100% + ${DROPDOWN_GAP}px);
        `}

  ${props => props.isOpen && css`
    border-color: ${primaryColor};
    box-shadow: 0 0 0 1px ${primaryColor};
  `}
`;

const SearchContainer = styled.div`
  padding: 0.75rem;
  border-bottom: 1px solid #333;
  position: relative;
  
  .automation-form-dropdown-multi & {
    padding: 0.6rem;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.5rem 0.5rem 2rem;
  font-size: 0.95rem;
  border: none;
  border-radius: ${borderRadius};
  outline: none;
  transition: all 0.2s ease;
  background-color: ${darkBackground};
  color: ${lightText};
  
  &:focus {
    box-shadow: 0 0 0 1px ${primaryColor};
  }
  
  &::placeholder {
    color: ${disabledText};
  }
`;

const SearchIcon = styled.svg`
  position: absolute;
  left: 1.25rem;
  top: 1.4rem;
  width: 16px;
  height: 16px;
  color: ${lightText};
`;

const OptionsList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #333;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #555;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #777;
  }
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  padding: 0.7rem 1rem;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background-color: #333;
  }
  
  ${props => props.isSelected && css`
    background-color: #333;
    
    &:hover {
      background-color: #444;
    }
  `}
  
  .automation-form-dropdown-multi & {
    font-size: 0.9rem;
  }
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 1.5px solid ${props => props.isSelected ? primaryColor : '#555'};
  border-radius: 4px;
  margin-right: 0.75rem;
  background-color: ${props => props.isSelected ? primaryColor : 'transparent'};
  transition: all 0.15s ease;
  position: relative;
  
  &:hover {
    border-color: ${props => props.isSelected ? primaryColor : '#777'};
  }
  
  svg {
    width: 12px;
    height: 12px;
    color: white;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const OptionLabel = styled.span`
  font-size: 0.95rem;
  color: ${lightText};
  
  .automation-form-dropdown-multi & {
    font-size: 0.9rem;
  }
`;

const NoResults = styled.div`
  padding: 1rem;
  color: ${disabledText};
  text-align: center;
  font-size: 0.95rem;
`;

// Update styling for selected tag pills
const SelectedTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  max-width: calc(100% - 20px);
  overflow: hidden;
  padding-top: 0.6rem;
  padding-bottom: 0.5rem;
`;

const SelectedTag = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #333;
  border: 1px solid #444;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 0.8rem;
  color: ${lightText};
  margin-right: 5px;
  margin-bottom: 3px;
  white-space: nowrap;
  
  .automation-form-dropdown-multi & {
    background-color: #333;
    border-color: #444;
    color: ${lightText};
  }
`;

const TagRemoveButton = styled.span`
  margin-left: 5px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${primaryColor};
  
  &:hover {
    color: ${primaryColor};
    opacity: 0.8;
  }
`;

const ClearAllButton = styled.div`
  font-size: 0.8rem;
  color: ${primaryColor};
  padding: 0.5rem 1rem;
  border-top: 1px solid #333;
  cursor: pointer;
  text-align: center;
  
  &:hover {
    background-color: #333;
  }
`;

const StyledMultiSelectWithSearch = ({
  options = [],
  value = [],
  onChange,
  placeholder = 'Search...',
  isDisabled = false,
  labelKey = 'label',
  valueKey = 'value',
  showSelectAll = true,
  className = '',
  id = '',
  name = '',
  classNames = {},
  showTags = false,
  hideAnyOption = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState(''); // Immediate input value
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [openUpward, setOpenUpward] = useState(false);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(DROPDOWN_MAX_HEIGHT);
  const wrapperRef = useRef(null);

  const updateDropdownPlacement = () => {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - DROPDOWN_GAP;
    const spaceAbove = rect.top - DROPDOWN_GAP;
    const shouldOpenUpward =
      spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;

    setOpenUpward(shouldOpenUpward);
    setDropdownMaxHeight(
      Math.max(
        160,
        Math.min(
          DROPDOWN_MAX_HEIGHT,
          shouldOpenUpward ? spaceAbove : spaceBelow
        )
      )
    );
  };

  // Reset search term when dropdown is closed
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setInputValue(''); // Also reset the immediate input value
      
      // Only update filteredOptions if the current value would actually change
      const newFilteredOptions = hideAnyOption && Array.isArray(options) && options.length > 0
        ? options.filter(option => option && option[labelKey] !== 'Any')
        : options;
        
      // Compare current filteredOptions with new value to prevent unnecessary updates
      const currentOptionsStr = JSON.stringify(filteredOptions);
      const newOptionsStr = JSON.stringify(newFilteredOptions);
      
      if (currentOptionsStr !== newOptionsStr) {
        setFilteredOptions(newFilteredOptions);
      }
    }
  }, [isOpen]);

  // Toggle dropdown open/closed
  const toggleDropdown = () => {
    if (isDisabled) return;

    if (!isOpen) {
      updateDropdownPlacement();
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleReposition = () => updateDropdownPlacement();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isOpen]);

  // Filter options based on search term
  useEffect(() => {
    // Skip if options isn't an array or is empty
    if (!Array.isArray(options) || options.length === 0) {
      if (filteredOptions.length !== 0) {
        setFilteredOptions([]);
      }
      return;
    }
    
    let newFilteredOptions;
    if (searchTerm.trim() === '') {
      // Apply hideAnyOption filter if needed
      newFilteredOptions = hideAnyOption
        ? options.filter(option => option && option[labelKey] !== 'Any')
        : options;
    } else {
      newFilteredOptions = options
        .filter(option => hideAnyOption ? (option && option[labelKey] !== 'Any') : true)
        .filter(option => {
          if (!option || typeof option !== 'object' || !option[labelKey]) {
            return false;
          }
          return option[labelKey].toLowerCase().includes(searchTerm.toLowerCase());
        });
    }
    
    // Compare current filteredOptions with new value to prevent unnecessary updates
    const currentOptionsStr = JSON.stringify(filteredOptions);
    const newOptionsStr = JSON.stringify(newFilteredOptions);
    
    if (currentOptionsStr !== newOptionsStr) {
      setFilteredOptions(newFilteredOptions);
    }
  }, [searchTerm]);
  
  // Update filtered options when options or related props change
  useEffect(() => {
    // Skip if options isn't an array or is empty
    if (!Array.isArray(options) || options.length === 0) {
      if (filteredOptions.length !== 0) {
        setFilteredOptions([]);
      }
      return;
    }
    
    // Only run this when options, hideAnyOption or labelKey change
    const newFilteredOptions = searchTerm.trim() === ''
      ? (hideAnyOption
          ? options.filter(option => option && option[labelKey] !== 'Any')
          : options)
      : options
          .filter(option => hideAnyOption ? (option && option[labelKey] !== 'Any') : true)
          .filter(option => {
            if (!option || typeof option !== 'object' || !option[labelKey]) {
              return false;
            }
            return option[labelKey].toLowerCase().includes(searchTerm.toLowerCase());
          });
    
    // Compare current filteredOptions with new value to prevent unnecessary updates
    const currentOptionsStr = JSON.stringify(filteredOptions);
    const newOptionsStr = JSON.stringify(newFilteredOptions);
    
    if (currentOptionsStr !== newOptionsStr) {
      setFilteredOptions(newFilteredOptions);
    }
  }, [options, hideAnyOption, labelKey]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const debouncedSearch = useRef(
    debounce((term) => {
      setSearchTerm(term);
    }, 300)
  ).current;

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSearch(value);
  };

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Helper function to get a unique identifier for an option
  const getOptionUniqueId = (option) => {
    if (!option) return '';
    // If statusId exists, use name+statusId as unique identifier
    if ('statusId' in option && option.statusId !== undefined) {
      return `${option.name}-${option.statusId}`;
    }
    // Otherwise fallback to regular comparison
    return option[valueKey] || option[labelKey] || option.name || '';
  };

  const isSelected = (option) => {
    if (!option || !Array.isArray(value) || value.length === 0) {
      return false;
    }
    
    // For options with statusId, check both name and statusId
    if ('statusId' in option && option.statusId !== undefined) {
      return value.some(item => 
        item && 
        item.name === option.name && 
        'statusId' in item && 
        item.statusId === option.statusId
      );
    }
    
    if (option[valueKey] && value.some(item => item && item[valueKey] === option[valueKey])) {
      return true;
    }
    
    if ((valueKey === 'value' || !option[valueKey]) && 'name' in option) {
      return value.some(item => 
        item && (
          (item.name && item.name === option.name) || 
          (item.value && item.value === option.name) ||
          (item.label && item.label === option.name))
      );
    }
    
    if (labelKey in option && value.some(item => item && item[labelKey] === option[labelKey])) {
      return true;
    }
    
    return value.some(item => item && JSON.stringify(item) === JSON.stringify(option));
  };

  const toggleOption = (option) => {
    if (isDisabled || !option) return;

    const selected = isSelected(option);
    const newValue = selected
      ? (Array.isArray(value) ? value.filter(item => {
          if (!item) return true;
          
          // For options with statusId, check both name and statusId
          if ('statusId' in option && option.statusId !== undefined && 
              item.name === option.name && 
              'statusId' in item && 
              item.statusId === option.statusId) {
            return false;
          }
          
          if (option[valueKey] && item[valueKey] === option[valueKey]) return false;
          if ((valueKey === 'value' || !option[valueKey]) && 'name' in option) {
            if ((item.name && item.name === option.name) || 
                (item.value && item.value === option.name) ||
                (item.label && item.label === option.name)) {
              return false;
            }
          }
          if (labelKey in option && item[labelKey] === option[labelKey]) return false;
          if (JSON.stringify(item) === JSON.stringify(option)) return false;
          return true;
        }) : [])
      : [...(Array.isArray(value) ? value : []), option];

    onChange(newValue);
  };
  
  const toggleAll = () => {
    if (isDisabled) return;

    // Handle empty options array
    if (!Array.isArray(options) || options.length === 0) {
      onChange([]);
      return;
    }

    // Filter out "Any" option when selecting all if hideAnyOption is true
    const selectableOptions = hideAnyOption 
      ? options.filter(option => option && option[labelKey] !== 'Any')
      : options;

    // If no selectable options, just return empty array
    if (selectableOptions.length === 0) {
      onChange([]);
      return;
    }

    // Check if all selectable options are already selected
    const allSelected = 
      Array.isArray(value) && 
      value.length > 0 && 
      selectableOptions.every(option => 
        value.some(item => {
          if (!item || !option) return false;
          if (item[valueKey] === option[valueKey]) return true;
          if (valueKey === 'value' && 'name' in option && item.name === option.name) return true;
          if (labelKey in option && item[labelKey] === option[labelKey]) return true;
          return false;
        })
      );

    if (allSelected) {
      onChange([]);
    } else {
      onChange([...selectableOptions]);
    }
  };

  // Handle the special "Any" option if present
  const handleAnyOption = () => {
    const anyOption = options.find(option => option[labelKey] === 'Any');
    if (anyOption) {
      onChange([anyOption]);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  // Format selected values for display
  const getDisplayValue = () => {
    if (!Array.isArray(value) || value.length === 0) return placeholder;
    
    // If all non-Any options are selected
    const selectableOptions = hideAnyOption 
      ? (Array.isArray(options) && options.length > 0 
          ? options.filter(option => option && option[labelKey] !== 'Any') 
          : [])
      : (Array.isArray(options) ? options : []);
      
    if (selectableOptions.length > 0 && value.length === selectableOptions.length) {
      return 'All items selected';
    }
    
    if (value.length === 1) {
      return value[0] && value[0][labelKey] ? value[0][labelKey] : placeholder;
    }
    return `${value.length} items selected`;
  };

  // Handle removing a specific tag
  const removeTag = (option, e) => {
    e.stopPropagation();
    const newValue = value.filter(item => {
      // For options with statusId, need to match both name and statusId exactly
      if ('statusId' in option && option.statusId !== undefined) {
        // Only remove if both name and statusId match
        if (item.name === option.name && 'statusId' in item && item.statusId === option.statusId) {
          return false; // Remove this item
        }
        // Otherwise keep it, even if only name matches
        return true;
      }
      
      // For options without statusId, use the original logic
      if (item[valueKey] === option[valueKey]) return false;
      if (valueKey === 'value' && 'name' in option) {
        if ((item.name && item.name === option.name) || 
            (item.value && item.value === option.name) ||
            (item.label && item.label === option.name)) {
          return false;
        }
      }
      if (labelKey in option && item[labelKey] === option[labelKey]) return false;
      return true;
    });
    onChange(newValue);
  };

  return (
    <Wrapper className={className} ref={wrapperRef} id={id} name={name}>
      <Header 
        isOpen={isOpen} 
        isDisabled={isDisabled}
        onClick={toggleDropdown}
      >
        {showTags && value && value.length > 0 ? (
          <SelectedTagsContainer>
            {value.slice(0, 3).map((option, index) => (
              <SelectedTag key={`tag-${index}`}>
                {option[labelKey]}
                <TagRemoveButton onClick={(e) => removeTag(option, e)}>×</TagRemoveButton>
              </SelectedTag>
            ))}
            {value.length > 3 && (
              <SelectedTag>+{value.length - 3} more</SelectedTag>
            )}
          </SelectedTagsContainer>
        ) : (
          <Display isEmpty={value && value.length === 0}>{getDisplayValue()}</Display>
        )}
        <ArrowWrapper>
          <Arrow 
            rotated={isOpen}
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </Arrow>
        </ArrowWrapper>
      </Header>

      {isOpen && (
        <Dropdown
          openUpward={openUpward}
          maxHeight={dropdownMaxHeight}
        >
          <SearchContainer>
            <SearchIcon 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search"
              onChange={handleInputChange}
              onClick={(e) => e.stopPropagation()}
              value={inputValue}
              key={`search-input-${isOpen}`}
            />
          </SearchContainer>

          <OptionsList>
            {/* Select All option */}
            {showSelectAll && options.length > 0 && (
              <Option
                onClick={toggleAll}
                isSelected={value.length === (hideAnyOption 
                  ? options.filter(option => option[labelKey] !== 'Any').length 
                  : options.length)}
              >
                <Checkbox isSelected={value.length === (hideAnyOption 
                  ? options.filter(option => option[labelKey] !== 'Any').length 
                  : options.length)}>
                  {value.length === (hideAnyOption 
                    ? options.filter(option => option[labelKey] !== 'Any').length 
                    : options.length) && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  )}
                </Checkbox>
                <OptionLabel>Select All</OptionLabel>
              </Option>
            )}

            {/* Any option if available and not hidden */}
            {!hideAnyOption && options.some(option => option[labelKey] === 'Any') && (
              <Option
                onClick={handleAnyOption}
                isSelected={value.length === 1 && value[0][labelKey] === 'Any'}
              >
                <Checkbox isSelected={value.length === 1 && value[0][labelKey] === 'Any'}>
                  {value.length === 1 && value[0][labelKey] === 'Any' && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  )}
                </Checkbox>
                <OptionLabel>Any</OptionLabel>
              </Option>
            )}

            {/* Regular options */}
            {filteredOptions
              .filter(option => hideAnyOption ? option[labelKey] !== 'Any' : true)
              .map((option, index) => (
              <Option
                key={getOptionUniqueId(option) + '-' + index}
                onClick={() => toggleOption(option)}
                isSelected={isSelected(option)}
              >
                <Checkbox isSelected={isSelected(option)}>
                  {isSelected(option) && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  )}
                </Checkbox>
                <OptionLabel>{option[labelKey]}</OptionLabel>
              </Option>
            ))}

            {/* No results message */}
            {filteredOptions.length === 0 && (
              <NoResults>No results found</NoResults>
            )}
          </OptionsList>
          
          {/* Clear all button */}
          {value.length > 0 && (
            <ClearAllButton onClick={() => onChange([])}>
              Clear All
            </ClearAllButton>
          )}
        </Dropdown>
      )}
    </Wrapper>
  );
};

StyledMultiSelectWithSearch.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  isDisabled: PropTypes.bool,
  labelKey: PropTypes.string,
  valueKey: PropTypes.string,
  showSelectAll: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  classNames: PropTypes.object,
  showTags: PropTypes.bool,
  hideAnyOption: PropTypes.bool
};

export default StyledMultiSelectWithSearch; 